import { Button, Divider, Spin, message } from "antd";
import { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import ContentLayoutManagement from "../../../../layouts/ContentLayoutManagement";
import HeaderContent from "../../../../layouts/HeaderContent";
import CardItemInCart from "../../../../components/VMRS/Cart/CardItemInCart";
import UnifiedDynamicForm from "../../../../components/VMRS/ExploreTasking/components/form/UnifiedDynamicForm";

import { RootState } from "../../../../store/store";
import { getGeneralFormOrderArchiveAsync, getFormOrderArchiveByProviderCodeAsync } from "../../../../store/reducers/orderSlice";
import { setSelectedImages, setArrIdImage } from "../../../../store/reducers/cartSlice";
import { showError, showSuccess } from "../../../../helpers/toast";
import { getErrorMsg } from "../../../../helpers/common";
import { ESttausCode } from "../../../../enums";
import { UniversalMap } from "../../../../components/VMRS/MapAdapter/UniversalMap";
import NoData from "../../../../components/NoData";

const transformGeneralSchema = (apiData: any) =>
{
  return [
    {
      step_id: "general_step",
      name: apiData?.name,
      schema: apiData?.form_schema?.schema,
      ui: apiData?.ui_schema?.ui,
    },
  ];
};
const CreateArchiveOrder = () =>
{
  const { t } = useTranslation();
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();

  const selectedItems = useSelector(
    (state: RootState) => state.cartSlice.selectedItems,
  );

  const [loading, setLoading] = useState(false);
  const [loadingSchemas, setLoadingSchemas] = useState(false);
  const [formData, setFormData] = useState<any>({});
  const [openProviders, setOpenProviders] = useState<Record<string, boolean>>(
    {},
  );
  const [engine, setEngine] = useState<any>(null);
  const [generalWorkflow, setGeneralWorkflow] = useState<any>(null);
  const [providerWorkflowsData, setProviderWorkflowsData] = useState<Record<string, any>>({});

  const [generalSteps, setGeneralSteps] = useState<any[]>([]);

  useEffect(() =>
  {
    if (!selectedItems?.length) return;

    const fetchSchema = async () =>
    {
      try
      {
        // @ts-ignore
        const res = await dispatch(getSchemaGeneralArchiveAsync()).unwrap();

        const steps = transformGeneralSchema(res?.data?.data);

        setGeneralSteps(steps);
      } catch (err)
      {
        showError(t(getErrorMsg(err)));
      }
    };

    fetchSchema();
  }, [selectedItems]);

  // =============================
  // GROUP ITEMS BY PROVIDER
  // =============================
  const itemsByProvider = useMemo(() =>
  {
    const groups: Record<string, any[]> = {};
    selectedItems?.forEach((item: any) =>
    {
      const key = item.provider_code?.toLowerCase() || "unknown";
      if (!groups[key]) groups[key] = [];
      groups[key].push(item);
    });
    return groups;
  }, [selectedItems]);

  // =============================
  // FETCH SCHEMAS
  // =============================
  useEffect(() =>
  {
    (dispatch(getGeneralFormOrderArchiveAsync()) as any)
      .unwrap()
      .then((res: any) =>
      {
        console.log(res, 'res');
        debugger
        setGeneralWorkflow(res?.data?.data)
      })
      .catch(() => { });
  }, []);

  useEffect(() =>
  {
    const providerCodes = Object.keys(itemsByProvider);
    if (providerCodes.length === 0) return;

    setLoadingSchemas(true);
    Promise.all(
      providerCodes.map((code) =>
        dispatch((getFormOrderArchiveByProviderCodeAsync as any)({ providerCode: code })).unwrap()
      )
    )
      .then((responses) =>
      {
        console.log(responses, 'responses');
        debugger
        const map: Record<string, any> = {};
        providerCodes.forEach((code, i) =>
        {
          map[code] = responses[i]?.data?.data;
        });
        setProviderWorkflowsData(map);
      })
      .catch(() => { })
      .finally(() => setLoadingSchemas(false));
  }, [itemsByProvider]);

  // =============================
  // STEPS
  // =============================
  // const generalSteps = useMemo(
  //   () => generalWorkflow?.workflow?.steps ?? [],
  //   [generalWorkflow]
  // );

  const providerSteps = useMemo(() =>
  {
    const result: any[] = [];
    Object.entries(providerWorkflowsData).forEach(([key, wf]: any) =>
    {
      wf?.workflow?.steps?.forEach((step: any) =>
      {
        result.push({ ...step, providerKey: key });
      });
    });
    return result;
  }, [providerWorkflowsData]);

  // =============================
  // INIT DEFAULT FORM DATA
  // =============================
  useEffect(() =>
  {
    const defaults: any = {};

    // general
    generalSteps.forEach((step: any) =>
    {
      step.ui?.order?.forEach((field: string) =>
      {
        const config = step.ui.options[field];
        if (config?.default !== undefined)
        {
          defaults[field] = config.default;
        }
      });
    });

    // provider
    providerSteps.forEach((step: any) =>
    {
      const key = step.providerKey;
      if (!defaults[key]) defaults[key] = {};

      step.ui?.order?.forEach((field: string) =>
      {
        const config = step.ui.options[field];
        if (config?.default !== undefined)
        {
          defaults[key][field] = config.default;
        }
      });
    });

    setFormData(defaults);
  }, [generalSteps, providerSteps]);

  // =============================
  // INIT COLLAPSE STATE
  // =============================
  useEffect(() =>
  {
    const init: any = {};
    Object.keys(itemsByProvider).forEach((k) =>
    {
      init[k] = true;
    });
    setOpenProviders(init);
  }, [itemsByProvider]);

  const toggleProvider = (key: string) =>
  {
    setOpenProviders((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // =============================
  // VALIDATE
  // =============================
  const validate = () =>
  {
    const errors: string[] = [];

    const isEmpty = (val: any) =>
      val === undefined || val === null || val === "";

    // ===== GENERAL =====
    for (const step of generalSteps)
    {
      const required = step.schema?.required || [];

      for (const field of required)
      {
        if (isEmpty(formData[field]))
        {
          errors.push(`General: ${field}`);
        }
      }
    }

    // ===== PROVIDERS =====
    for (const step of providerSteps)
    {
      const required = step.schema?.required || [];
      const providerKey = step.providerKey;

      for (const field of required)
      {
        const value = formData?.[providerKey]?.[field];

        if (isEmpty(value))
        {
          errors.push(`${providerKey}: ${field}`);
        }
      }
    }

    if (errors.length > 0)
    {
      const newOpen = { ...openProviders };

      providerSteps.forEach((step) =>
      {
        const providerKey = step.providerKey;
        const required = step.schema?.required || [];

        for (const field of required)
        {
          const value = formData?.[providerKey]?.[field];
          if (value === undefined || value === null || value === "")
          {
            newOpen[providerKey] = true;
          }
        }
      });

      setOpenProviders(newOpen);

      showError(t("Missing required fields") + ": " + errors.join(", "));
      return false;
    }

    return true;
  };

  // =============================
  // SUBMIT
  // =============================
  const handleSubmit = async () =>
  {
    console.log(formData, "formData");
    if (!validate()) return;

    try
    {
      setLoading(true);

      const payload: any = { ...formData };

      Object.keys(itemsByProvider).forEach((key) =>
      {
        payload[key] = {
          ...formData[key],
          item_ids: itemsByProvider[key].map((i: any) => i.id),
        };
      });

      console.log(payload);
    } catch (err)
    {
      showError(t(getErrorMsg(err)));
    } finally
    {
      setLoading(false);
    }
  };

  // =============================
  // UI
  // =============================
  return (
    <ContentLayoutManagement
      header={
        <HeaderContent
          breadcrumb={[{ title: "Cart" }, { title: t("Create Order") }]}
        />
      }
    >
      <Spin spinning={loading || loadingSchemas}>
        <div className="w-full h-full flex bg-[var(--neutral-1000)] text-white">
          {/* LEFT PANEL */}
          <div className="w-5/12 h-full bg-[#0F172A] text-white flex flex-col border-r border-gray-800">
            {selectedItems?.length > 0 ? (
              <>
                <div className="overflow-auto h-[calc(100%-65px)] pb-6">
                  {/* ===== GENERAL ===== */}
                  <div className="space-y-4 pt-6">
                    <p className="uppercase text-sm px-6">
                      {t("General Information")}
                    </p>

                    {generalSteps.map((step: any, idx: number) => (
                      <UnifiedDynamicForm
                        key={idx}
                        step={{ schema: step.schema, ui: step.ui }}
                        formData={formData}
                        setFormData={setFormData}
                        display="grid"
                      />
                    ))}
                  </div>

                  <Divider />

                  {/* ===== PROVIDERS ===== */}
                  {Object.entries(itemsByProvider).map(
                    ([providerKey, items], index) => (
                      <div key={providerKey} className="space-y-4">
                        {/* HEADER */}
                        <div
                          className="flex justify-between items-center cursor-pointer px-6"
                          onClick={() => toggleProvider(providerKey)}
                        >
                          <p className="uppercase text-sm">
                            {providerKey} ({items.length})
                          </p>

                          <span>{openProviders[providerKey] ? "▲" : "▼"}</span>
                        </div>

                        {/* CARD LIST */}
                        <div className="space-y-2 px-6">
                          {items.map((item: any) => (
                            <CardItemInCart
                              key={item.id}
                              item={item}
                              showCheckBox={false}
                            />
                          ))}
                        </div>

                        {/* FORM */}
                        {openProviders[providerKey] && (
                          <div>
                            {providerSteps
                              .filter((s) => s.providerKey === providerKey)
                              .map((step: any, idx: number) => (
                                <UnifiedDynamicForm
                                  key={idx}
                                  step={{ schema: step.schema, ui: step.ui }}
                                  formData={formData[providerKey] || {}}
                                  setFormData={(updater: any) =>
                                    setFormData((prev: any) => ({
                                      ...prev,
                                      [providerKey]:
                                        typeof updater === "function"
                                          ? updater(prev[providerKey] || {})
                                          : {
                                            ...(prev[providerKey] || {}),
                                            ...updater,
                                          },
                                    }))
                                  }
                                />
                              ))}
                          </div>
                        )}
                        {index <
                          Object.entries(itemsByProvider)?.length - 1 && (
                            <div className="px-6">
                              <Divider />
                            </div>
                          )}
                      </div>
                    ),
                  )}
                </div>

                {/* FOOTER */}
                <div className="p-4 border-t border-gray-800 flex justify-between w-full">
                  <Button className="btn-outline" onClick={() => navigate(-1)}>
                    {t("Back")}
                  </Button>
                  <Button
                    className="w-[85px] !border-none !bg-[var(--primary-4)] text-[var(--primary-950)]"
                    type="primary"
                    onClick={handleSubmit}
                  >
                    {t("Submit")}
                  </Button>
                </div>
              </>
            ) : (
              <>
                <NoData
                  extraButton={
                    <Button
                      onClick={() => navigate("/cart")}
                      className="w-[125px] !border-none !bg-[var(--primary-4)] text-[var(--primary-950)] mt-2"
                    >
                      {t("Back to cart")}
                    </Button>
                  }
                />
              </>
            )}
          </div>

          {/* RIGHT MAP */}
          <div className="flex-1">
            <UniversalMap
              mapType="maplibre"
              showEngine={false}
              showDraw={false}
              showDrawAOI={false}
              showMeasure={false}
              view={{
                center: [108.23653393802581, 16.1039225743402],
                zoom: 5,
              }}
              showBaseMap={true}
              showMapControl={true}
              onMapReady={setEngine}
            />
          </div>
        </div>
      </Spin>
    </ContentLayoutManagement>
  );
};

export default CreateArchiveOrder;
